import { useId } from 'react'
import {
    Droplet,
    FlaskConical,
    GlassWater,
    Thermometer,
    TriangleAlert,
    Waves,
    Zap,
} from 'lucide-react'
import type { IrrigationDevice } from '@/lib/api/irrigation-devices-api'
import type { LucideIcon } from 'lucide-react'

import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

/** Bản ghi cũ hơn mốc này (phút) thì báo số liệu đã cũ. */
export const SENSOR_STALE_MINUTES = 30

type Sensors = IrrigationDevice['sensors']

type SensorRow = {
    key: keyof Sensors
    label: () => string
    icon: LucideIcon
    iconClass: string
    /** Đơn vị: chỉ ghi cái màn hình của bộ tưới ghi (API không gửi đơn vị). */
    unit?: string
}

const ROWS: Array<SensorRow> = [
    {
        key: 'soil_moisture',
        label: m.irrigation_sensor_soil_moisture,
        icon: Droplet,
        iconClass: 'text-sky-600 dark:text-sky-400',
        unit: '%',
    },
    {
        key: 'soil_temperature',
        label: m.irrigation_sensor_soil_temperature,
        icon: Thermometer,
        iconClass: 'text-red-600 dark:text-red-400',
        unit: '°C',
    },
    {
        key: 'ph',
        label: m.irrigation_sensor_ph,
        icon: FlaskConical,
        iconClass: 'text-violet-600 dark:text-violet-400',
    },
    {
        key: 'ec',
        label: m.irrigation_sensor_ec,
        icon: Zap,
        iconClass: 'text-amber-600 dark:text-amber-400',
    },
    {
        key: 'salinity',
        label: m.irrigation_sensor_salinity,
        icon: Waves,
        iconClass: 'text-slate-600 dark:text-slate-400',
    },
    {
        key: 'tds',
        label: m.irrigation_sensor_tds,
        icon: GlassWater,
        iconClass: 'text-teal-600 dark:text-teal-400',
    },
]

/** Bộ tưới có ít nhất một thông số đất trong bản ghi gần nhất. */
export function hasSensors(device: IrrigationDevice): boolean {
    return Object.values(device.sensors).some((v) => v !== null)
}

/** Bản ghi đọc được nhưng cũ hơn {@link SENSOR_STALE_MINUTES} phút. */
export function isStale(device: IrrigationDevice): boolean {
    if (device.reading_state !== 'ok' || !device.received_at) return false
    const ageMs = Date.now() - new Date(device.received_at).getTime()
    return ageMs > SENSOR_STALE_MINUTES * 60_000
}

/**
 * Lưới 6 thông số đất (3 cột × 2 hàng) như màn hình gắn trên bộ tưới. Số lấy
 * từ cùng bản ghi với trạng thái bơm, nên giờ "Cập nhật lúc" ngay trên cũng là
 * giờ của các số này. Thiết bị không gửi giá trị nào thì ô đó hiện "—".
 */
export function IrrigationSensorGrid({ device }: { device: IrrigationDevice }) {
    const titleId = useId()
    const stale = isStale(device)
    return (
        <section aria-labelledby={titleId} className="space-y-2">
            <h3
                id={titleId}
                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
                {m.irrigation_sensors_title()}
            </h3>
            {stale && (
                <p
                    role="status"
                    className={twJoin(
                        'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs',
                        'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
                    )}
                >
                    <TriangleAlert aria-hidden className="size-3.5 shrink-0" />
                    {m.irrigation_sensors_stale({
                        minutes: SENSOR_STALE_MINUTES,
                    })}
                </p>
            )}
            <dl className="grid grid-cols-3 gap-2">
                {ROWS.map((row) => (
                    <SensorTile
                        key={row.key}
                        row={row}
                        value={device.sensors[row.key]}
                    />
                ))}
            </dl>
        </section>
    )
}

/** Cỡ chữ theo độ dài: ô chỉ rộng ~68px ở điện thoại 360px (3 cột trên thẻ). */
export function valueSizeClass(text: string): string {
    if (text.length <= 6) return 'text-lg'
    if (text.length <= 7) return 'text-base'
    if (text.length <= 8) return 'text-sm'
    if (text.length <= 9) return 'text-xs'
    return 'text-[11px]'
}

function SensorTile({ row, value }: { row: SensorRow; value: number | null }) {
    const Icon = row.icon
    const text = value === null ? '—' : `${formatQty(value)}${row.unit ?? ''}`
    return (
        <div className="min-w-0 rounded-md border bg-background px-2 py-2 sm:px-2.5">
            {/* Icon giữ nguyên cỡ; nhãn dài xuống tối đa 2 dòng thay vì bị cắt. */}
            <dt className="flex min-w-0 items-start gap-1.5 text-xs text-muted-foreground">
                <Icon
                    aria-hidden
                    className={twJoin('size-3.5 shrink-0', row.iconClass)}
                />
                <span className="line-clamp-2 break-words leading-tight">
                    {row.label()}
                </span>
            </dt>
            {/* Luôn hiện đủ số, không cắt "…": số dài thì chữ nhỏ lại để vừa
                một dòng, quá dài nữa mới xuống dòng. */}
            <dd
                className={twJoin(
                    'mt-0.5 font-semibold leading-tight tabular-nums [overflow-wrap:anywhere]',
                    valueSizeClass(text),
                    value === null && 'font-medium text-muted-foreground',
                )}
            >
                {text}
            </dd>
        </div>
    )
}
